// Wrapper around libflacjs (loaded as a global by index.html). Encodes a
// Float32 mono PCM buffer to a self-contained FLAC stream.
//
// We don't import libflacjs as a module because its npm main entry pulls in
// Node's `path` module, which can't be bundled for the browser. Instead, the
// build step copies dist/libflac.min.wasm.{js,wasm} into web/public/libflac/
// and a <script> tag in index.html exposes it as window.Flac.

interface FlacLib {
  isReady?: () => boolean;
  on: (event: 'ready', cb: () => void) => void;
  create_libflac_encoder: (
    sampleRate: number,
    channels: number,
    bps: number,
    level: number,
    totalSamples: number,
  ) => number;
  init_encoder_stream: (
    encoder: number,
    writeCb: (buf: Uint8Array, bytes: number, samples: number, currentFrame: number) => void,
    metadataCb?: unknown,
    oggSerial?: number,
    clientData?: number,
  ) => number;
  FLAC__stream_encoder_process_interleaved: (
    encoder: number,
    samples: Int32Array,
    samplesPerChannel: number,
  ) => boolean;
  FLAC__stream_encoder_finish: (encoder: number) => boolean;
  FLAC__stream_encoder_delete: (encoder: number) => void;
}

declare global {
  interface Window { Flac?: FlacLib; }
}

let readyPromise: Promise<FlacLib> | null = null;
function ready(): Promise<FlacLib> {
  if (!readyPromise) {
    readyPromise = new Promise<FlacLib>((resolve, reject) => {
      const F = window.Flac;
      if (!F) {
        reject(new Error(
          'libflacjs not loaded — check that /libflac/libflac.min.wasm.js is reachable',
        ));
        return;
      }
      if (F.isReady && F.isReady()) resolve(F);
      else F.on('ready', () => resolve(F));
    });
  }
  return readyPromise;
}

export interface EncodeOptions {
  sampleRate: number;
  channels: number;
  bitsPerSample?: number;
  compressionLevel?: number;
}

export async function encodeFlac(
  samplesFloat32: Float32Array,
  opts: EncodeOptions,
): Promise<Uint8Array> {
  const Flac = await ready();

  const channels = opts.channels;
  const bps = opts.bitsPerSample ?? 16;
  const level = opts.compressionLevel ?? 5;
  const samplesPerChannel = Math.floor(samplesFloat32.length / channels);

  const encoder = Flac.create_libflac_encoder(
    opts.sampleRate, channels, bps, level, samplesPerChannel,
  );
  if (!encoder) throw new Error('libflac: failed to create encoder');

  const chunks: Uint8Array[] = [];
  const initStatus = Flac.init_encoder_stream(encoder, (buf, bytes) => {
    chunks.push(new Uint8Array(buf.subarray(0, bytes)));
  });
  if (initStatus !== 0) {
    Flac.FLAC__stream_encoder_delete(encoder);
    throw new Error(`libflac: init_encoder_stream failed (status ${initStatus})`);
  }

  const maxPos = (1 << (bps - 1)) - 1;
  const maxNeg = -(1 << (bps - 1));
  const ints = new Int32Array(samplesFloat32.length);
  for (let i = 0; i < samplesFloat32.length; i++) {
    let s = samplesFloat32[i];
    if (s > 1) s = 1;
    else if (s < -1) s = -1;
    ints[i] = s < 0 ? Math.round(s * -maxNeg) : Math.round(s * maxPos);
  }

  const ok = Flac.FLAC__stream_encoder_process_interleaved(encoder, ints, samplesPerChannel);
  if (!ok) {
    Flac.FLAC__stream_encoder_delete(encoder);
    throw new Error('libflac: process_interleaved failed');
  }
  Flac.FLAC__stream_encoder_finish(encoder);
  Flac.FLAC__stream_encoder_delete(encoder);

  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}
